/**
 * Validates all intake tabs when the intake loads so the app knows their
 * validity without the user opening each tab. Populates intakeTabValidation
 * in the store; individual tabs still update when visited (live form state).
 */

import { useEffect } from 'react';
import { useStore } from '../store';
import type { IntakeRecord } from '../store/slices/intakeSlice';
import { IntakeTabValidationKey } from '../store/slices/intakeSlice';
import {
  schedulingStepFormSchema,
  getSchedulingStepFormSchema,
} from '../schemas/schedulingStepFormSchema';
import type { SchedulingStepClinicContext } from '../schemas/schedulingStepFormSchema';
import {
  useIntakeFormSchema,
  getSortedSections,
  getRequiredFields,
} from '../config/intakeFormSchema';
import { runValidation } from '../components/intake/validators';
import { getSelectedSchedulingEmrFromFlags } from '../utils/intakeSchedulingEmr';
import {
  intakeAuthorizationVisitCounts,
  isAuthorizationDraftValid,
} from '../components/intake/authorizationDraftValidation';
import type { IntakeFormSchema } from '../config/intakeFormSchema';

function validateExtractionFromData(
  schema: IntakeFormSchema,
  formData: Record<string, unknown>
): boolean {
  const requiredFields = getRequiredFields(schema);
  const hasMissingRequired = requiredFields.some(field => {
    const value = formData[field.field_id];
    return (
      value == null ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0)
    );
  });
  if (hasMissingRequired) return false;

  const sortedSections = getSortedSections(schema);
  for (const [, section] of sortedSections) {
    for (const field of section.content) {
      if (field.validator_fn) {
        const value = formData[field.field_id];
        const error = runValidation(
          field.validator_fn,
          typeof value === 'string'
            ? value
            : value != null
              ? String(value)
              : undefined
        );
        if (error) return false;
      }
    }
  }
  return true;
}

function toStr(v: string | Date | null | undefined): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  return String(v);
}

function validateSchedulingFromIntake(intake: IntakeRecord): boolean {
  if (intake.is_new_patient === false) {
    // Existing patients should not be blocked by scheduling validation.
    return true;
  }
  const r = intake as unknown as Record<string, unknown>;
  const payload = {
    kinnser_create_episode: r.kinnser_create_episode ?? null,
    roger_create_episode: r.roger_create_episode ?? null,
    push_to_kinnser: r.push_to_kinnser ?? null,
    push_to_roger: r.push_to_roger ?? null,
    kinnser_visit_type: r.kinnser_visit_type ?? null,
    kinnser_creation_type: r.kinnser_creation_type ?? null,
    roc_date: (r.roc_date as string | null | undefined) ?? null,
    recert_date: (r.recert_date as string | null | undefined) ?? null,
    kinnser_mrn: (r.kinnser_mrn as string | null | undefined) ?? null,
    kinnser_type_of_soc: r.kinnser_type_of_soc ?? null,
    kinnser_soc_date: toStr(
      r.kinnser_soc_date as string | Date | null | undefined
    ),
    kinnser_episode_start_date: toStr(
      r.kinnser_episode_start_date as string | Date | null | undefined
    ),
    kinnser_clinician_id: r.kinnser_clinician_id ?? null,
    kinnser_clinical_care_manager_id:
      r.kinnser_clinical_care_manager_id ?? null,
    kinnser_case_manager_id: r.kinnser_case_manager_id ?? null,
    kinnser_oasis_approver_id: r.kinnser_oasis_approver_id ?? null,
    kinnser_pt_approver_id: r.kinnser_pt_approver_id ?? null,
    kinnser_ot_approver_id: r.kinnser_ot_approver_id ?? null,
    kinnser_st_approver_id: r.kinnser_st_approver_id ?? null,
    kinnser_primary_scheduler_id: r.kinnser_primary_scheduler_id ?? null,
    kinnser_secondary_scheduler_id: r.kinnser_secondary_scheduler_id ?? null,
    roger_type_of_soc: r.roger_type_of_soc ?? null,
    soc_date: toStr(r.soc_date as string | Date | null | undefined),
    episode_start_date: toStr(
      r.episode_start_date as string | Date | null | undefined
    ),
    roger_clinician_id: r.roger_clinician_id ?? null,
    roger_case_manager_id: r.roger_case_manager_id ?? null,
    // Pass is_new_patient so schema can relax visit/creation type and episode fields for new patients
    is_new_patient: intake.is_new_patient ?? null,
  };
  const result = schedulingStepFormSchema.safeParse(payload);
  return result.success;
}

/**
 * Validate scheduling step using clinic context (e.g. secondary scheduler required when clinic has that list).
 * Use this when clinic data is available so the result matches what the Scheduling tab form enforces.
 */
export function validateSchedulingFromIntakeWithClinicData(
  intake: IntakeRecord,
  clinicData: SchedulingStepClinicContext
): boolean {
  const r = intake as unknown as Record<string, unknown>;
  if (intake.is_new_patient === false) {
    if (r.push_to_kinnser === true && clinicData?.use_kinnser_wrm === true) {
      const vt = r.kinnser_visit_type;
      const ct = r.kinnser_creation_type;
      if (vt === 'roc') return false;
      if (vt === 'soc' && ct === 'current_episode') return false;
      if (vt === 'soc' && ct === 'new_episode') return false;
    }
    return true;
  }
  const payload = {
    kinnser_create_episode: r.kinnser_create_episode ?? null,
    roger_create_episode: r.roger_create_episode ?? null,
    push_to_kinnser: r.push_to_kinnser ?? null,
    push_to_roger: r.push_to_roger ?? null,
    kinnser_visit_type: r.kinnser_visit_type ?? null,
    kinnser_creation_type: r.kinnser_creation_type ?? null,
    roc_date: (r.roc_date as string | null | undefined) ?? null,
    recert_date: (r.recert_date as string | null | undefined) ?? null,
    kinnser_mrn: (r.kinnser_mrn as string | null | undefined) ?? null,
    kinnser_type_of_soc: r.kinnser_type_of_soc ?? null,
    kinnser_soc_date: toStr(
      r.kinnser_soc_date as string | Date | null | undefined
    ),
    kinnser_episode_start_date: toStr(
      r.kinnser_episode_start_date as string | Date | null | undefined
    ),
    kinnser_clinician_id: r.kinnser_clinician_id ?? null,
    kinnser_clinical_care_manager_id:
      r.kinnser_clinical_care_manager_id ?? null,
    kinnser_case_manager_id: r.kinnser_case_manager_id ?? null,
    kinnser_oasis_approver_id: r.kinnser_oasis_approver_id ?? null,
    kinnser_pt_approver_id: r.kinnser_pt_approver_id ?? null,
    kinnser_ot_approver_id: r.kinnser_ot_approver_id ?? null,
    kinnser_st_approver_id: r.kinnser_st_approver_id ?? null,
    kinnser_primary_scheduler_id: r.kinnser_primary_scheduler_id ?? null,
    kinnser_secondary_scheduler_id: r.kinnser_secondary_scheduler_id ?? null,
    roger_type_of_soc: r.roger_type_of_soc ?? null,
    soc_date: toStr(r.soc_date as string | Date | null | undefined),
    episode_start_date: toStr(
      r.episode_start_date as string | Date | null | undefined
    ),
    roger_clinician_id: r.roger_clinician_id ?? null,
    roger_case_manager_id: r.roger_case_manager_id ?? null,
    // Pass is_new_patient so schema can relax visit/creation type and episode fields for new patients
    is_new_patient: intake.is_new_patient ?? null,
  };
  const schema = getSchedulingStepFormSchema(clinicData);
  const result = schema.safeParse(payload);
  return result.success;
}

export interface PriorAuthValidationOptions {
  /** The intake's payer requires visit authorization. */
  isPriorAuthRequired?: boolean;
  /**
   * The intake uses RogerEMR (EMR-343). Kinnser intakes render the legacy
   * free-text Prior Auth form, which has no visit counts to validate, so the
   * tab never blocks the Push to EMR gate for them.
   *
   * Omit it and the intake's own push flags decide. That is coarser than the
   * full agency+clinic gate (`usesRogerEmrForIntake`) but never wrong in the
   * blocking direction: it only skips Roger rules for an intake explicitly
   * headed to Kinnser. Callers that know the real answer should pass it.
   */
  usesRogerEmr?: boolean;
}

/** Shared validation: compute all tab validity from intake (and schema for extraction) and write to store. */
export function computeAndSetIntakeTabValidation(
  selectedIntake: IntakeRecord | null,
  intakeFormSchema: IntakeFormSchema | null | undefined,
  setIntakeTabValid: (
    tab: IntakeTabValidationKey,
    value: boolean | null
  ) => void,
  { isPriorAuthRequired = true, usesRogerEmr }: PriorAuthValidationOptions = {}
): void {
  if (!selectedIntake) return;

  const rogerRulesApply =
    usesRogerEmr ??
    getSelectedSchedulingEmrFromFlags(selectedIntake) !== 'kinnser';

  // Eligibility and Ready to Call have no form validations — only "mark complete".
  setIntakeTabValid(IntakeTabValidationKey.Eligibility, true);
  setIntakeTabValid(
    IntakeTabValidationKey.PriorAuth,
    !rogerRulesApply ||
      !isPriorAuthRequired ||
      selectedIntake.create_authorization_on_onboarding === false ||
      // Always recompute from the persisted row. An invalid live draft is only
      // ever held in PriorAuthStep's local state, and that state dies with the
      // component when its tab unmounts — preserving the `false` past that
      // point would strand the Push to EMR gate on an error nobody can see.
      isAuthorizationDraftValid(intakeAuthorizationVisitCounts(selectedIntake))
  );
  setIntakeTabValid(IntakeTabValidationKey.ReadyToCall, true);
  setIntakeTabValid(
    IntakeTabValidationKey.Scheduling,
    validateSchedulingFromIntake(selectedIntake)
  );
  if (intakeFormSchema) {
    const formData = selectedIntake as unknown as Record<string, unknown>;
    setIntakeTabValid(
      IntakeTabValidationKey.Extraction,
      validateExtractionFromData(intakeFormSchema, formData)
    );
  }
}

export function useIntakeTabValidationOnLoad(
  selectedIntake: IntakeRecord | null,
  usesRogerEmr?: boolean
): void {
  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);
  const { schema: intakeFormSchema } = useIntakeFormSchema();
  const isPriorAuthRequired = Boolean(
    selectedIntake?.agency_insurance_id &&
    selectedIntake.agency_insurance?.visit_authorization_required
  );

  useEffect(() => {
    computeAndSetIntakeTabValidation(
      selectedIntake,
      intakeFormSchema ?? null,
      setIntakeTabValid,
      {
        isPriorAuthRequired,
        usesRogerEmr,
      }
    );
  }, [
    selectedIntake,
    intakeFormSchema,
    setIntakeTabValid,
    isPriorAuthRequired,
    usesRogerEmr,
  ]);
}
